import React, { useEffect, useState } from "react";
import BookingApi from "../../../API/Apis/BookingApi";
import { BookingDetails } from "../../../@types/Booking/bookingDetails";
import { BookingFormProps } from "./BookingForm.props";
import { NewLocationDetails } from "../../../@types/DTO/Locations";
import { useTranslation } from "react-i18next";
import { formatTheme } from "../../../config/MuiColorThemes";
import createTheme from "@mui/material/styles/createTheme";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import { useBookingFormState } from "./UseBookingFormState";
import { BookingFormContent } from "./BookingFormContent";

// Extracted hook for location data
const useLocationData = () => {
  const [locationDetail, setLocationDetail] = useState<NewLocationDetails[]>(
    []
  );

  useEffect(() => {
    const fetchLocations = async () => {
      const data = await BookingApi.getReservationLocations();
      const sortedLocations: any[] = [];

      if (data.data.data !== "") {
        Object.keys(data.data).forEach((dealerId) => {
          const dealerData = data.data[dealerId];
          const locations = Object.keys(dealerData)
            .filter(
              (key) =>
                key !== "dealerName" &&
                key !== "dealerReservationSource" &&
                key !== "showLoggedInDealerLoc"
            )
            .map((locationName) => ({
              ...dealerData[locationName],
              locationName,
              dealerId,
              dealerName: dealerData.dealerName,
              dealerReservationSource: dealerData.dealerReservationSource,
              showLoggedInDealerLoc: dealerData.showLoggedInDealerLoc,
            }));
          sortedLocations.push(...locations);
        });

        sortedLocations.sort((a, b) =>
          a.locationName.localeCompare(b.locationName)
        );
        setLocationDetail(sortedLocations || []);
        localStorage.removeItem("isBlocklistCustomer");
      } else {
        setLocationDetail([]);
      }
    };

    fetchLocations();
  }, []);

  return locationDetail;
};

// Extracted hook for parent window communication
const useParentCommunication = () => {
  const [targetOrigin, setTargetOrigin] = useState("*");

  const getParentOrigin = () => {
    try {
      if (
        window.location.ancestorOrigins &&
        window.location.ancestorOrigins.length > 0
      ) {
        return window.location.ancestorOrigins[0];
      }
      if (document.referrer) {
        return new URL(document.referrer).origin;
      }
      return "";
    } catch (error) {
      console.error("Error in getParentOrigin:", error);
      return "";
    }
  };

  useEffect(() => {
    const parentOrigin = getParentOrigin();
    const allowedOrigins = new Set([
      "https://mbrent.mercedes-benz.it",
      "https://bookingmerfina.mbrent.mercedes-benz.it",
      "https://mbrent.mercedes-benz.it/congaratulation",
      "https://merfinatestbooking.myrent.it",
      "https://mauroc147.myrent.it",
    ]);

    setTargetOrigin(
      parentOrigin && allowedOrigins.has(parentOrigin) ? parentOrigin : "*"
    );
  }, []);

  const requestParentResize = (isLogged: string | null) => {
    const isSmallScreen = window.innerWidth <= 768;
    window.parent.postMessage(
      {
        event_id: "iframeHeight",
        data: {
          iframeHeight: isSmallScreen && isLogged === "true" ? 1100 : 850,
        },
      },
      targetOrigin
    );
  };

  const scrollToTop = () => {
    window.parent.postMessage(
      {
        event_id: "scrollToTop",
      },
      targetOrigin
    );
  };

  return { requestParentResize, scrollToTop, targetOrigin };
};

// Extracted function to create booking location details
const createBookingLocationDetails = (
  locationName: string,
  locationDetail: NewLocationDetails[]
) => {
  const location = locationDetail.find((e) => e.locationName === locationName);

  return {
    name: locationName,
    code: location?.code,
    dealerId: location?.dealerId,
    dealerName: location?.dealerName,
    locationDescription: location?.description,
    email: location?.email,
    latitude: location?.latitude,
    longitude: location?.longitude,
    locationWithDealerName: location?.locationName,
    showLoggedInDealerLoc: location?.showLoggedInDealerLoc,
  };
};

// Extracted function to create booking details
const createBookingDetails = (
  pickupLocation: string,
  reservationDestination: string,
  reservationStartDate: Date | null,
  reservationEndDate: Date | null,
  locationDetail: NewLocationDetails[]
): BookingDetails => {
  const now = Date.now();
  const selectedPickupLocation = locationDetail.find(
    (e) => e.locationName === pickupLocation
  );

  return {
    pickUpDateAndTime: reservationStartDate?.getTime() || now,
    dropOffDateAndTime: reservationEndDate?.getTime() || now,
    dropOffLocationDetails: createBookingLocationDetails(
      reservationDestination,
      locationDetail
    ),
    dropOffLocationName: reservationDestination,
    pickUpLocationDetails: createBookingLocationDetails(
      pickupLocation,
      locationDetail
    ),
    pickUpLocationName: pickupLocation,
    reservationSource: selectedPickupLocation?.dealerReservationSource,
    showPics: true,
    showOptionalImage: true,
    showVehicleParameter: true,
    showVehicleExtraImage: true,
    showBookingDiscount: true,
    payLaterFeeAmt: "",
    isPayLaterFee: false,
  };
};

// Updated useFormSubmission hook
const useFormSubmission = (
  pickupLocation: string,
  reservationDestination: string,
  reservationStartDate: Date | null,
  reservationEndDate: Date | null,
  locationDetail: NewLocationDetails[],
  onSubmit: (details: BookingDetails) => void,
  scrollToTop: () => void
) => {
  return (e: React.FormEvent) => {
    e.preventDefault();

    const bookingDetails = createBookingDetails(
      pickupLocation,
      reservationDestination,
      reservationStartDate,
      reservationEndDate,
      locationDetail
    );

    scrollToTop();
    window.scrollTo(0, 50);
    onSubmit(bookingDetails);
  };
};
const useScreenSize = () => {
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 768);
  const [istabletScreen, setIstabletScreen] = useState(
    window.innerWidth >= 768 && window.innerWidth <= 1200
  );

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 768);
      setIstabletScreen(window.innerWidth >= 768 && window.innerWidth <= 1200);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return { isSmallScreen, istabletScreen };
};

const BookingForm = ({
  onSubmit = () => {},
  className = "",
}: BookingFormProps): JSX.Element => {
  const dynamicPalette = formatTheme();
  const dynamicTheme = createTheme(dynamicPalette);
  const { t: translate } = useTranslation();
  const isLogged = localStorage.getItem("isUserLoggedin");
  const { requestParentResize, scrollToTop } = useParentCommunication();
  const locationDetail = useLocationData();

  const {
    pickupLocation,
    setPickupLocation,
    reservationDestination,
    setReservationDestination,
    reservationStartDate,
    setStartDate,
    reservationEndDate,
    setEndDate,
    maxRentalAllowedDay,
    minRentalAllowedDay,
  } = useBookingFormState();

  const { isSmallScreen, istabletScreen } = useScreenSize();

  const [isDroppingAtSameLocation] = useState<string>(
    translate("drop at same location")
  );

  const handleSubmit = useFormSubmission(
    pickupLocation,
    reservationDestination,
    reservationStartDate,
    reservationEndDate,
    locationDetail,
    onSubmit,
    scrollToTop
  );

  useEffect(() => {
    requestParentResize(isLogged);
  }, [isLogged, requestParentResize]);

  const handleLocationChange = (e: string) => {
    if (isDroppingAtSameLocation.length) {
      setReservationDestination(e);
    }
    setPickupLocation(e);
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <ThemeProvider theme={dynamicTheme}>
      <main className={`h-full flex justify-center box-border ${className}`}>
        <form
          style={{ width: isSmallScreen ? "368px" : "" }}
          onSubmit={handleSubmit}
        >
          <BookingFormContent
            pickupLocation={pickupLocation}
            // reservationDestination={reservationDestination}
            reservationStartDate={reservationStartDate}
            reservationEndDate={reservationEndDate}
            locationDetail={locationDetail}
            minRentalAllowedDay={minRentalAllowedDay}
            maxRentalAllowedDay={maxRentalAllowedDay}
            isSmallScreen={isSmallScreen}
            istabletScreen={istabletScreen}
            translate={translate}
            onPickupLocationChange={handleLocationChange}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            // isDroppingAtSameLocation={isDroppingAtSameLocation}
          />
        </form>
      </main>
    </ThemeProvider>
  );
};

// Extracted form content component

// Helper function for disabled days
// Extracted LocationInput component
// Extracted SubmitButton component

export default BookingForm;
